import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _, { extend } from 'lodash';
import { Button } from '@material-ui/core';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from '../../../utilities/PgmUtilities';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { buildPageId } from '../AdminLinkUtils';
import FormSection from '../../form/FormSection';
import { HTTP_STATUS_400, useDataService } from '../../../services/ApiDataService';
import {
    NOTIFICATIONS_CONFIG_API_URL,
    NOTIFICATIONS_CONFIG_QUERY_URL,
    NOTIFICATIONS_FUNCTION_NAME,
    POLICIES,
    POLICY_NAMES,
    POLICY_TABS,
} from './NotificationPolicyConstants';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';
import NotificationPolicySetting from './NotificationPolicySetting';

const defaultValues = {};

export const VALID_TABS = POLICY_TABS;
export const DEFAULT_TAB = POLICY_NAMES.serviceTest;

function prepareSavedData(dataToSave, formData) {
    return _.extend({}, dataToSave, { _id: formData._id });
}

export default function NotificationPolicyDetail() {
    const { tab: activeTab } = useParams();
    const navigate = useNavigate();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(
        _.extend(USE_FORM_DEFAULT_OPTIONS, {
            defaultValues,
        })
    );
    const { reset, setError, handleSubmit } = methods;
    const breadcrumbs = [{ text: i18n._(t`${POLICIES[activeTab].title} Notifications`) }];
    const dataService = useDataService();
    const formSaveService = useFormSaveService();
    const [isIpmDisabled] = useIsIpmDisabled();
    const [notifySelectionIsDirty, setNotifySelectionIsDirty] = useState(false);
    const [snmpTrapSelectionIsDirty, setSnmpTrapSelectionIsDirty] = useState(false);

    const handleTabChange = (newTab) => {
        if (newTab === null) return;
        navigate(`../${newTab}`);
    };

    const postFindHandler = (response) => {
        return response[0] || _.first(response) || response;
    };

    const { initialized, finding, formData, resetForm } = useFind({
        url: NOTIFICATIONS_CONFIG_API_URL,
        postFindHandler,
        queryFunc: queryNotificationConfig,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}),
    });

    function queryNotificationConfig() {
        return dataService.get(NOTIFICATIONS_CONFIG_QUERY_URL, { type: activeTab }).then((response) => {
            if (_.isArray(response)) {
                return _.first(response);
            }

            return response;
        });
    }

    const dirtyCheck = useCallback(
        (isDirty) => {
            return isDirty || notifySelectionIsDirty || snmpTrapSelectionIsDirty;
        },
        [notifySelectionIsDirty, snmpTrapSelectionIsDirty]
    );

    const handleSaveError = (errorObject) => {
        const noSelectionError = 'no_notification_selection';

        let responseErrorMessage;
        if (errorObject.getStatus && errorObject.getStatus() === HTTP_STATUS_400) {
            const responseText = _.get(errorObject, 'responseBody');
            if (responseText && responseText.message === noSelectionError) {
                responseErrorMessage = t`Email or SNMP Trap notifications need to be setup`;
            }
        }

        throw extend(errorObject, {
            responseErrorMessage,
        });
    };

    const handleFormSubmit = (dataToSave = {}) => {
        const putData = prepareSavedData(dataToSave, formData);

        return formSaveService.save({
            url: NOTIFICATIONS_CONFIG_API_URL,
            saveFunc: () =>
                dataService.put(`${NOTIFICATIONS_CONFIG_API_URL}/${putData._id}`, putData).then(() => {
                    setNotifySelectionIsDirty(false);
                    setSnmpTrapSelectionIsDirty(false);
                    resetForm();
                }),
            dataToSave: putData,
            setError,
            saveErrorHandler: handleSaveError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(NOTIFICATIONS_FUNCTION_NAME)}
            actions={
                <Actions>
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving}
                    isDirtyFunc={dirtyCheck}
                >
                    <FormSection>
                        <Tabs value={activeTab} onChange={(e, index) => handleTabChange(index)}>
                            <Tab label="Service Test" value={POLICY_NAMES.serviceTest} />
                            <Tab label="Site" value={POLICY_NAMES.site} />
                            <Tab label="Business Service" value={POLICY_NAMES.businessService} />
                            {!isIpmDisabled && <Tab label="Interface" value={POLICY_NAMES.interface} />}
                            {!isIpmDisabled && <Tab label="Network Device" value={POLICY_NAMES.device} />}
                        </Tabs>
                        <NotificationPolicySetting
                            activeTab={activeTab}
                            data={formData}
                            setNotifySelectionIsDirty={setNotifySelectionIsDirty}
                            setSnmpTrapSelectionIsDirty={setSnmpTrapSelectionIsDirty}
                        />
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
